import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Circle, ArrowRight, Sparkle, X, Confetti } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Dashboard onboarding checklist.
 *
 * Hits `GET /api/onboarding/status` and renders a progress card with actionable
 * items. When a user completes all four items, the checklist collapses (per-user
 * dismissed state is held in localStorage so it doesn't pop back after refresh).
 */
export function OnboardingChecklist() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("clariva_onboarding_dismissed") === "1"; }
    catch { return false; }
  });
  const [loadingSamples, setLoadingSamples] = useState(false);
  const nav = useNavigate();

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
    } catch (e) {
      /* silently skip — user may be a viewer without permission */
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, []);

  const loadSamples = async () => {
    setLoadingSamples(true);
    try {
      await api.post("/onboarding/load-samples", {});
      toast.success("Sample data loaded — have a poke around");
      await fetchStatus();
      // Hint to the rest of the app to re-fetch (caller can listen to this event)
      window.dispatchEvent(new CustomEvent("clariva:samples-loaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load samples");
    } finally { setLoadingSamples(false); }
  };

  const handleItem = (item) => {
    if (item.action === "load_samples") return loadSamples();
    if (item.link) nav(item.link);
  };

  const dismiss = () => {
    try { localStorage.setItem("clariva_onboarding_dismissed", "1"); } catch {}
    setDismissed(true);
  };

  if (loading || !status) return null;
  const { checklist = [], progress = { complete: 0, total: 0 } } = status;
  const allDone = progress.complete >= progress.total;
  // Hide when all done AND the user has dismissed it (or it has auto-dismissed silently next visit).
  if (allDone && dismissed) return null;

  const pct = progress.total > 0 ? Math.round((progress.complete / progress.total) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="relative rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] overflow-hidden"
      data-testid="onboarding-checklist"
    >
      <div className="p-5 sm:p-6 flex items-start gap-4">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-[var(--accent)]/15 text-[var(--accent)] flex items-center justify-center">
          {allDone ? <Confetti size={22} weight="duotone" /> : <Sparkle size={22} weight="duotone" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-base font-semibold tracking-tight" data-testid="onboarding-title">
                {allDone ? "You're all set" : "Finish setting up Clariva"}
              </h3>
              <p className="text-xs text-[var(--text-3)] mt-0.5">
                {allDone
                  ? "Every onboarding step is complete. Nice work."
                  : `${progress.complete} of ${progress.total} done — ${pct}%`}
              </p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="text-[var(--text-3)] hover:text-[var(--text-1)] p-1 rounded-md"
              aria-label="Dismiss onboarding checklist"
              data-testid="onboarding-dismiss-btn"
            >
              <X size={16} />
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-1.5 rounded-full bg-[var(--surface-2)] overflow-hidden">
            <div
              className="h-full bg-[var(--accent)] transition-all duration-500"
              style={{ width: `${Math.max(pct, 2)}%` }}
              data-testid="onboarding-progress-bar"
            />
          </div>

          <ul className="mt-5 space-y-2">
            {checklist.map((item) => (
              <li
                key={item.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] hover:border-[var(--border-strong)] transition px-3 py-2.5"
                data-testid={`onboarding-item-${item.id}`}
              >
                <div className="flex items-start gap-3 min-w-0">
                  {item.done ? (
                    <CheckCircle size={18} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                  ) : (
                    <Circle size={18} className="text-[var(--text-3)] mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className={`text-sm font-medium ${item.done ? "text-[var(--text-3)] line-through" : "text-[var(--text-1)]"}`}>
                      {item.title}
                    </div>
                    <div className="text-xs text-[var(--text-3)] mt-0.5 line-clamp-2">
                      {item.description}
                    </div>
                  </div>
                </div>
                {!item.done && (
                  <Button
                    size="sm"
                    variant={item.action === "load_samples" ? "outline" : "default"}
                    onClick={() => handleItem(item)}
                    disabled={item.action === "load_samples" && loadingSamples}
                    className="shrink-0 gap-1"
                    data-testid={`onboarding-cta-${item.id}`}
                  >
                    {item.action === "load_samples" && loadingSamples ? "Loading…" : item.cta}
                    <ArrowRight size={12} />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}
